import * as React from 'react';
import moment from "moment";
import Translate from 'react-translate-component';
import { baseOnSalesDiscountPrice, basePrice, determineFree, discountPrice, discountValue } from './pricingCalculations';
import { cx, priceFormat } from './util';
import styles from "./main.module.scss";
import { translateService } from '../../services/TranslateService';
import "./payment.scss"
import {useContext, useEffect, useState} from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import Coupon from './Coupon';
import { GlobalStore } from '../../reduxStore';
import dataContext, { DataContext } from '../../contexts/dataContext';
import {
  isCompanyNameValid,
  isCompanyVatIdValid,
  useEmailConsent,
  useServiceAgreementValidation
} from '../../hooks/useCustomerHooks';
import {CustomerAgreeCheckboxInput, CustomerRadioInput} from "../CustomerInput/CustomerInput";


interface ServiceRowProps {
  included: Boolean,
  label: React.ReactNode;
  content: React.ReactNode;
  discount?: React.ReactNode;
  back : Function;
  scrollLocation: string;
}

function ServiceRow({ included, label, content, discount, back, scrollLocation }: ServiceRowProps) {
  const dataContext = useContext(DataContext);

  return <tr>
    <td>{label} </td>
    <td className={included ? '' : styles.notIncludedText}>
      {included ? content :
          <Translate content={'vehicleSearch1.notIncluded'} />}
    </td>
    <td>

      { !dataContext.reservationCreated ? !included ?
          <div scroll-to-item="true" scroll-to="#additionalInsuranceAnchor{vehicle.id}">
            <button className={cx("btn", styles.orangeAddServicesButton)} onClick={(e)=>{back(2, scrollLocation);
              dataContext.sendGaEvent('reservation', 'finalStepAddServices', '', 0);}}>
              <Translate content={'vehicleSearch1.addService'} />
            </button>
          </div>
          : discount : null }
    </td>
  </tr>
}

interface PaymentPropsI {
  back: Function;
  next: Function;
  reservationCreated: boolean;
  setRedirectHome : Function;
  reservationLength : number;
}

export enum paymentMethod {
  PAYTRAIL=0,
  QUOTA=3,
  FREE=6,
  INVOICE=2
}

const banks = [
  {name: 'aktia'},
  {name: 'alandsbanken'},
  {name: 'danske'},
  {name: 'handelsbanken'},
  {name: 'mastercard'},
  {name: 'mobilepay'},
  {name: 'nordea'},
  {name: 'nordea-business'},
  {name: 'omasp'},
  {name: 'osuuspankki'},
  {name: 'pop'},
  {name: 'saastopankki'},
  {name: 'spankki'},
  {name: 'visa'},
  {name: 'visa-electron'}
];

export default function PaymentInfo({ setRedirectHome, reservationCreated, back, next, reservationLength }: PaymentPropsI) {
  const { rootUrl, services, changeServices, customer, clearSelections, vehicle, oneWayRes, sendGaEvent, getReservationDays,
    searchParameters, changeCustomer, authHashLogin, user } = React.useContext(DataContext);
  const [paymentCountDownTimerInterval, setTimer] = useState<NodeJS.Timeout>();
  const [paymentCountDownTimer, setPaymentCountDownTimer] = useState(" 15:00 ");
  const [paymentCountDownTimerStart, setPaymentCountDownTimerStart] = useState(translateService.translate('vehicleSearch1.translationStartCountDownTimer1'));
  const [paymentCountDownTimerEnd, setPaymentCountDownTimerEnd] = useState(translateService.translate('vehicleSearch1.translationStartCountDownTimer2'));
  const [paymentCountDownTimerNotice, setPaymentCountDownTimerNotice] = useState(translateService.translate('vehicleSearch1.translationStartCountDownTimer3'));
  const [paymentCountDownTimerExpired, setPaymentCountDownTimerExpired] = useState(cx(styles['payment-footer-lastminutenotice'], styles.center));
  const [paymentCountDownTimerClock, setPaymentCountDownTimerClock] = useState(styles.countDownTimerClock);
  const dispatch = useDispatch();
  const coupon = useSelector((s: GlobalStore) => s.chauffeurReducer.coupon);
  const couponValid = coupon?.valid && vehicle?.pricingData.coupon && !vehicle?.onsale && !vehicle.privateData?.vip;
  const freeReservation = determineFree(vehicle,services, reservationLength);
  const eurosPerExtraKm = '0.20'; //TODO fetch this constant from the backend instead
  const [payFree, setPayFree] = useState(false);
  const [payQuota, setPayQuota] = useState(false);
  const [payPaytrail, setPayPaytrail] = useState(false);
  const [payInvoice, setPayInvoice] = useState(false);
  const corporateCustomer = (isCompanyNameValid(customer?.companyName) && isCompanyVatIdValid(customer?.companyVatNumber)) ? true : false;
  const reservationDays = getReservationDays();
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const {agreeErrorString, agree, setPolicy, setLiability, setReturnAgree, setConditions, handleServiceAgreeCheckErr,
    acceptFieldRefs } = useServiceAgreementValidation(customer?.agree || false, "acceptField1", true);
  const {emailConsent, setEmailConsentInputs, emailConsentErrorString, handleEmailConsentCheckErr, emailConsent1FieldRef,
    emailConsent2FieldRef} = useEmailConsent({yes: false, no: false},
      "emailConsentField1", "emailConsentField2"); // must set both to unselected on start
  const [ submitValidationErrorString, setSubmitValidationErrorString ] = useState('');
  const [ saveInfoBtnClicked, setSaveInfoBtnClicked ] = useState(false);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(true); // start with true, check again when clicking 'pay res' button
  let countDownTimer = 15 * 60; // seconds

  // check if invoice payment method can be used
  let canUseInvoice = false;
  if (customer?.mayUseBankTransferAnytime &&
      moment(searchParameters?.startDate, 'L').diff(moment(), 'days') >=
      (customer?.relativeBankTransferDueDate < 0 ? (customer?.relativeBankTransferDueDate * -1) : 999)) {
    canUseInvoice = true;
  } else {
    canUseInvoice = false;
  }

  const service = (discount: boolean , free: boolean) => {
    if (free && discount) {
      changeServices({...services, paymentMethod: paymentMethod.FREE });
    }
    if (!free) {
      changeServices({...services,
        paymentMethod: ((canUseInvoice && corporateCustomer) || (customer?.mayUseQuota) ?
            undefined : paymentMethod.PAYTRAIL)});
    }
  }

  useEffect(() => {
    service(!!couponValid, freeReservation)
  }, [couponValid, freeReservation]);

  useEffect(() => {
    setPayFree(services.paymentMethod===paymentMethod.FREE)
    setPayQuota(services.paymentMethod===paymentMethod.QUOTA)
    setPayPaytrail(services.paymentMethod===paymentMethod.PAYTRAIL)
    setPayInvoice(services.paymentMethod===paymentMethod.INVOICE);
  }, [services]);

  useEffect(() => {
    if(!paymentMethodSelected && services.paymentMethod !== undefined) {
      setPaymentMethodSelected(true);
    }
  }, [paymentMethodSelected, services.paymentMethod])

  useEffect(()  => {
    if (payInvoice && !canUseInvoice) { // resets payment methods if invoice option selected but no longer available
      if (freeReservation) return; // case where invoice no longer available due to date changes + res is free
      changeServices({...services, "paymentMethod": (customer?.mayUseQuota) ? undefined : paymentMethod.PAYTRAIL})
    }
  }, [reservationDays, payInvoice, canUseInvoice, freeReservation])

  useEffect(() => {
    if (paymentCountDownTimerInterval)
      clearInterval(paymentCountDownTimerInterval);
    let timer = setInterval(() => {
      if (countDownTimer >= 0) {
        let minutes = Math.floor((countDownTimer % 3600) / 60);
        let seconds = Math.floor(countDownTimer % 60);

        let stringMinutes = minutes.toString();
        let stringSeconds = seconds.toString();
        if (minutes < 10) {
          stringMinutes = '0' + minutes;
        }
        if (seconds < 10) {
          stringSeconds = '0' + seconds;
        }
        //$scope.paymentCountDownTimer = $scope.translationStartCountDownTimer1 + " " + minutes + ":" + seconds + " " + $scope.translationStartCountDownTimer2;
        setPaymentCountDownTimer(" " + stringMinutes + ":" + stringSeconds + " ");
        setPaymentCountDownTimerClock(styles.countDownTimerClock);
        setPaymentCountDownTimerNotice(translateService.translate('vehicleSearch1.translationStartCountDownTimer3'));
        countDownTimer -= 1;
      } else {
        clearInterval(paymentCountDownTimerInterval);
        setPaymentCountDownTimer(translateService.translate('vehicleSearch1.translationStartCountDownTimer4'));
        setPaymentCountDownTimerStart('');
        setPaymentCountDownTimerEnd('');
        setPaymentCountDownTimerNotice(translateService.translate('vehicleSearch1.translationStartCountDownTimer5'));
        setPaymentCountDownTimerClock(styles.countDownTimerClockExpired);
        setPaymentCountDownTimerExpired(cx(styles['payment-footer-lastminutenotice-expired'], styles.center));
        sendGaEvent('reservation', 'Payment-timeout', '', 0);
      }
    }, 1000);
    setTimer(timer);
    return () => { clearInterval(timer) };
  }, []);

  useEffect(()=>{
    if (freeReservation)
      changeServices({...services, "paymentMethod":paymentMethod.FREE})
    else if ((canUseInvoice && corporateCustomer) || (customer?.mayUseQuota)) {
      // keep all unselected if more than 1 option available
      changeServices({...services, "paymentMethod": undefined})
    } else {
      changeServices({...services, "paymentMethod": paymentMethod.PAYTRAIL})
    }

    if(user?.emailConsent) {
      // do not ask for email consent if user previous chose to receive emails
      setEmailConsentInputs(true, "yes");
    }
  },[])

  useEffect(() => {
    changeCustomer({...customer, agree: agree,
      agree2: agree,
      emailConsent: emailConsent.yes ? "true" : "false",
      fairAccept: agree
    })

    if(agree && submitValidationErrorString) {
      setSubmitValidationErrorString('');
    }
  }, [agree, emailConsent, submitValidationErrorString])

  if(!vehicle){
    return(
        <div className={cx(styles.r24FullWidth)}>
          <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
            <span> Vehicle Information Not Found </span>
          </div>
        </div>
    )
  }

  const onCreateReservation = (bankName?: string) => {
    setPaymentSubmitted(true);

    const checkValidationFields = () => {
      // uses the hook functions to check errors and return component names for autoscroll if err exists
      if(handleServiceAgreeCheckErr()) { return handleServiceAgreeCheckErr() }
      if(handleEmailConsentCheckErr()) { return handleEmailConsentCheckErr() }
      return null
    }

    let conditionsFieldError = checkValidationFields();
    if (conditionsFieldError) {
      let inputField = document.querySelector('[name="' + conditionsFieldError + '"]');
      inputField?.scrollIntoView({behavior: "smooth", block: "center"})
      setSubmitValidationErrorString(translateService.translate("vehicleSearch1.invalidSaveInfo"));
    } else {
      setSubmitValidationErrorString('');
      next(bankName);
    }
  }

  return <>
    <div className={cx(styles.r24FullWidth)}>
      <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
        <table className={styles.r24PriceTable} style={{width: '100%'}}>
          <thead>
          <tr>
            <th><Translate content={'vehicleSearch1.services'} /></th>
            <th><Translate content={'vehicleSearch1.price'} /></th>
            <th >{(couponValid || vehicle.onsale) &&
                <Translate content={'vehicleSearch1.discountedPrice'}/>}</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td> {vehicle.vehicleModelData.manufacturer} {vehicle.vehicleModelData.name} </td>
            <td> {priceFormat(vehicle.pricingData.base.totalPrice / 100)} € </td>
            <td><strong>
              {vehicle.onsale ?
                  (priceFormat( (vehicle.onsale.price*vehicle.onsale.discountmultiplier) / 100) + ' €')
                  :
                  (couponValid && priceFormat((vehicle.pricingData.withCoupon?.totalPrice ?? 1) / 100) +' €')
              }</strong>
            </td>
          </tr>
          {
            oneWayRes ?
                services.kmRange === 100 &&
                <>
                  <td> <Translate content={'vehicleSearch1.onewayKmIncludedService'} /> </td>
                  <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                  {couponValid &&
                      <td>
                        <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>,&nbsp;
                        <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                      </td>
                  }
                </>
                : <>
                  <tr>
                    {(services.kmRange === 100 || services.kmRange === 400) &&
                        <td>{services.kmRange * reservationLength /*make 0 decimals*/} km</td>}
                    {(services.kmRange === 0 || services.kmRange === 1000) &&
                        <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                    {services.kmRange === 100 &&
                        <>
                          <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                          {couponValid &&
                              <td>
                                <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>,&nbsp;
                                <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                              </td>
                          }
                        </>}
                    {services.kmRange === 400 && <>
                      <td>{priceFormat(vehicle.pricingData.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€,&nbsp;
                        <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                      </td>
                      {couponValid &&
                          <td >
                            <strong>
                              { priceFormat(vehicle.pricingData.base.pricePerKilometer/100 *
                                  Math.max(services.kmRange - 100, 0) * reservationLength) } &nbsp;€
                            </strong>,&nbsp; <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                          </td>}
                    </>}
                    {services.kmRange === 1000 && <>
                      <td>{priceFormat(vehicle.pricingData.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€
                      </td>
                      {couponValid && <td>
                        <strong>
                          { priceFormat(vehicle.pricingData.base.pricePerKilometer/100 *
                              Math.max(services.kmRange - 100, 0) * reservationLength) } &nbsp;€
                        </strong>
                      </td>}
                    </>}
                    {services.kmRange === 0 && <>
                      <td>{priceFormat(vehicle.pricingData.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                      </td>
                      {couponValid && <td>
                        { priceFormat(vehicle.pricingData.withCoupon!.unlimitedKilometers/100 * reservationLength)} &nbsp;€
                      </td>}
                    </>}
                    {!couponValid && <td></td>}
                  </tr>
                </>
          }
          <ServiceRow included={services.insurance.includes('1030')} back={back} scrollLocation={'insurancesAndDeductibles'}
                      label={<>
                        <Translate content={'vehicleSearch1.fairdeductible'}/>&nbsp;
                        {priceFormat(vehicle.vehicleData.deductible.highDeductibleValue! / 100 * (services.export?4:1))} &nbsp;€
                      </> }
                      content={<><Translate content={'vehicleSearch1.includedInPrice'}/></>}
          />

          <ServiceRow included={services.insurance.includes('1053')} back={back} scrollLocation={'insurancesAndDeductibles'}
                      label={<><Translate content={'vehicleSearch.services.midDeductionText'} />&nbsp;
                        {priceFormat(vehicle.vehicleData.deductible.midDeductibleValue! / 100 * (services.export?4:1))} €</>}
                      content={<>{priceFormat(vehicle.pricingData.base.midDeductible / 100 * services.insuranceReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.midDeductible/100 * services.insuranceReservationLength)} €</strong>}
          />

          <ServiceRow included={services.insurance.includes('1028')} back={back} scrollLocation={'insurancesAndDeductibles'}
                      label={<>
                        <Translate content={'vehicleSearch1.deductible'} />&nbsp;
                        {priceFormat(vehicle.vehicleData.deductible.lowDeductibleValue! / 100 * (services.export?4:1))} €</>}
                      content={<>{priceFormat(vehicle.pricingData.base.lowDeductible / 100 * services.insuranceReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.lowDeductible/100 * services.insuranceReservationLength)} €</strong>}
          />

          <ServiceRow included={services.windshield} back={back} scrollLocation={'additionalInsurances'}
                      label={<Translate content={'vehicleSearch1.windshieldInsurance'} />}
                      content={<>{priceFormat(vehicle.pricingData.base.windshieldInsurance / 100 * services.windshieldReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.windshieldInsurance/100 * services.windshieldReservationLength)} €</strong>}
          />

          <ServiceRow included={services.tireInsurance}  back={back} scrollLocation={'additionalInsurances'}
                      label={<Translate content={'vehicleSearch1.tireInsurance'} />}
                      content={<>{priceFormat(vehicle.pricingData.base.tireInsurance / 100 * services.tireInsuranceReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.tireInsurance/100 * services.tireInsuranceReservationLength)} €</strong>}
          />

          {!oneWayRes ?
              <ServiceRow included={services.cancellationInsurance} back={back} scrollLocation={'additionalInsurances'}
                          label={<Translate content={'vehicleSearch1.cancellationInsurance'} />}
                          content={<>{priceFormat(vehicle.pricingData.base.cancellationInsurance / 100 * services.cancellationInsuranceReservationLength)} €</>}
                          discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.cancellationInsurance/100 * services.cancellationInsuranceReservationLength)} €</strong>}
              /> : null }

          <ServiceRow included={services.additionalDriver}   back={back} scrollLocation={'additionalDriver'}
                      label={<Translate content={'vehicleSearch1.additionalDriver'} />}
                      content={<>{priceFormat(vehicle.pricingData.base.additionalDriver / 100 * services.additionalDriverReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.additionalDriver/100 * services.additionalDriverReservationLength)} €</strong>}
          />

          {(vehicle.vehicleData.hasHitch && services.hook) &&
              <ServiceRow included={services.hook} back={back}  scrollLocation={'additionalServices'}
                          label={<Translate content={'vehicleSearch1.hook'} />}
                          content={<>{priceFormat(vehicle.pricingData.base.towHitch / 100 * services.hookReservationLength)} €</>}
                          discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.towHitch/100 * services.hookReservationLength)} €</strong>}/>
          }

          {services.hasHandtruck &&
              <ServiceRow included={services.handTruck} back={back} scrollLocation={'additionalServices'}
                          label={<Translate content={'vehicleSearch1.handTruck'} />}
                          content={<Translate content={'vehicleSearch1.includedInPrice'} />}
                          discount={couponValid && <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>}/>
          }

          { !oneWayRes && (vehicle.vehicleData.exportPermits.balticStates ||
              vehicle.vehicleData.exportPermits.scandinavia || vehicle.vehicleData.exportPermits.europe) ?
              <ServiceRow included={services.export} back={back}  scrollLocation={'export'}
                          label={<Translate content={'vehicleSearch1.export'} />}
                          content={<>{priceFormat(vehicle.pricingData.base.export / 100 * services.exportReservationLegth)} €</>}
                          discount={couponValid && <strong>{ priceFormat(vehicle.pricingData.withCoupon!.export/100 * services.exportReservationLegth)} €</strong>}/>
              : null
          }

          {services.co2compensation === '' &&
              <tr>
                <td><Translate content={'vehicleSearch1.noCO2Compensation'} /></td>
                <td className={styles.notIncludedText}><Translate content={'vehicleSearch1.notIncluded'} /></td>
                <td>
                  { !reservationCreated ?
                      <div scroll-to-item="true" scroll-to="#exportAnchor{vehicle.id}">
                        <button className={cx("btn", styles.orangeAddServicesButton)} onClick={(e)=>{back(2,'co2compensation0{vehicle.id}');
                          sendGaEvent('reservation', 'finalStepAddServices', '', 0);}}>
                          <Translate content={'vehicleSearch1.addService'} />
                        </button>
                      </div>
                      : null
                  }

                </td>
              </tr>
          }
          {services.co2compensation === '1047' &&
              <tr>
                <td><Translate content={'vehicleSearch1.miniCompensation'} /></td>
                <td>{priceFormat(vehicle.pricingData.base.hundredTwentyFiveGramsCOTwo! / 100 * services.co2CompensationReservationLength)} €</td>
                {couponValid &&
                    <td>
                      <strong>{ priceFormat(vehicle.pricingData.withCoupon!.hundredTwentyFiveGramsCOTwo!/100 * services.co2CompensationReservationLength)} €</strong>
                    </td>}
              </tr>
          }
          {services.co2compensation === '1048' &&
              <tr>
                <td><Translate content={'vehicleSearch1.basicCompensation'} /></td>
                <td>
                  { priceFormat(vehicle.pricingData.base.fiveHundredGramsCOTwo/100 * services.co2CompensationReservationLength)}
                </td>
                {couponValid &&
                    <td>
                      <strong>{ priceFormat(vehicle.pricingData.withCoupon!.fiveHundredGramsCOTwo/100 * services.co2CompensationReservationLength)} €</strong>
                    </td>}
              </tr>
          }
          {services.co2compensation === '1049' &&
              <tr>
                <td>
                  <Translate content={'vehicleSearch1.overCompensation'} />
                </td>
                <td>
                  {priceFormat(vehicle.pricingData.base.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                </td>
                {couponValid &&
                    <td>
                      {priceFormat(vehicle.pricingData.withCoupon!.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                    </td>
                }
              </tr>
          }
          {couponValid &&
              <tr>
                <td> <Translate content={'vehicleSearch1.discount'}/> </td>
                <td> 0 €</td>
                <td>
                  <strong> -{priceFormat(discountValue(vehicle,services,reservationLength)) } €</strong>
                </td>
              </tr>
          }
          <tr>
            <td><strong>
              <Translate with={{ type: moment().isBefore('2024-09-01') // before 1st Sept 2024 --> 24%, else 25.5% VAT
                    ? translateService.translate('vehicleSearch.vatPercent24') :
                    translateService.translate('vehicleSearch.vatPercent25')}}
                         content='vehicleSearch1.totalVat'  />
            </strong></td>
            <td>
              {priceFormat(basePrice(vehicle, services, reservationLength))} €
            </td>
            <td >
              {
                vehicle.onsale ?
                    <strong>{ priceFormat(baseOnSalesDiscountPrice(vehicle,services,reservationLength)) } €</strong>
                    :(
                        couponValid ?
                            <strong>{ priceFormat(discountPrice(vehicle,services,reservationLength)) } €</strong>
                            :
                            null
                    )
              }
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      {!vehicle.onsale && !reservationCreated && !oneWayRes && !vehicle.privateData?.vip
          && <Coupon carId={vehicle.id}/>}

      {(services.paymentMethod !== paymentMethod.PAYTRAIL && reservationCreated) ?
          (<div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)}>
                <div className={cx(styles.r24ChildFlex1)}>
                  <div className={cx(styles.borderAroundLightRed, styles.r24BottomMargin10)}>
                    <div className={cx(styles.r24FullWidth , styles.r24TopBottomMargin10)}>
                      <h4 className={cx("text-center", styles.r24ReservationSuccess)}>
                        <Translate with={{ resNum: reservationCreated }} content='vehicleSearch1.translationReservationDone' />
                      </h4>
                      <p className={cx("text-center", styles.r24ReservationSuccess)}>
                        <Translate  content={services.paymentMethod===paymentMethod.QUOTA ? 'vehicleSearch1.translationAfterQuota' : 'vehicleSearch1.translationAfterReservation'}/>
                      </p>
                      <br/>
                      <p className={cx("text-center", styles.r24ReservationSuccess)}>
                        <Translate  content={'vehicleSearch1.successfulQuotaOrFreeRes1'}/>
                        <a href={'https://m.24rent.fi'}> <Translate  content={'vehicleSearch1.successfulQuotaOrFreeRes2'}/> </a>
                        <Translate  content={'vehicleSearch1.successfulQuotaOrFreeRes3'}/>
                      </p>
                    </div>
                    <div className={cx(styles.r24FullWidth, styles.r24TopBottomMargin10)}>
                    </div>
                  </div>
                  <button className={cx("btn", styles.orangeButton)} onClick={() => {window.location.href = 'https://m.24rent.fi'}} >
                    <Translate  content={'vehicleSearch1.goToMatkaan'}/>
                  </button>
                  <br/>
                </div>
              </div>
          ) :
          (<>
            <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
              <div className={paymentCountDownTimerExpired}>
                <span className={styles.lastminutecounternotice1}>
                  {paymentCountDownTimerStart}
                  <span className={paymentCountDownTimerClock}>{paymentCountDownTimer}</span>
                  {paymentCountDownTimerEnd}
                </span>
                <span className={styles.lastminutecounternotice2}>{paymentCountDownTimerNotice}</span>
              </div>
            </div>
            <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable)}>
              <form className={styles['payment-modal']}>
                <div className={cx(styles.r24FullWidth, styles.r24AlingCenter,styles.r24Padding10,)}
                     style={{border: paymentMethodSelected ? "none" : "2px dotted red"}}
                     onChange={ (e) => changeServices({...services,
                       paymentMethod: Number(e.target? (e.target as HTMLInputElement).value : undefined)})}>
                  <div className={cx(styles.r24FullWidth,"mb-3")} id="selectPaymentMethod">
                    <span className={styles.creditCardInformation}><Translate content={'vehicleSearch1.payment'} /></span>
                  </div>

                  <div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.paymentSelectionError)}
                       hidden={paymentMethodSelected}>
                    {translateService.translate('payment.paymentRequired')} </div>

                  {(customer?.mayUseQuota && !freeReservation) &&
                      <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                        <div className={styles.paymentRow}>

                          <input type="radio" name="paymentMethodsSelection" id="quotaButton{vehicle.id}"
                                 checked={payQuota} readOnly value={paymentMethod.QUOTA} className={styles.r24CheckboxInput}/>
                          <label htmlFor="quotaButton{vehicle.id}" className={cx(styles.r24CheckBoxInputLabel)}>
                            <Translate content={'vehicleSearch1.quota'} />
                          </label>
                        </div>
                      </div>
                  }

                  {canUseInvoice && !freeReservation && corporateCustomer &&
                      <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                        <div className={styles.paymentRow}>
                          <input type="radio" name="paymentMethodsSelection" id="invoiceButton{vehicle.id}"
                                 value={paymentMethod.INVOICE} readOnly checked={payInvoice}
                                 className={cx(styles.r24CheckboxInput)}/>
                          <label htmlFor="invoiceButton{vehicle.id}" className={cx(styles.r24CheckBoxInputLabel)}>
                            <Translate content={'vehicleSearch1.invoice'} />
                          </label>
                        </div>
                      </div>
                  }

                  {!freeReservation &&
                      <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                        <div className={styles.paymentRow}>
                      <span>
                        <input type="radio" name="paymentMethodsSelection" id="bankButton{vehicle.id}"
                               checked={payPaytrail} readOnly value={paymentMethod.PAYTRAIL}
                               className={cx(styles.r24CheckboxInput)}/>
                        <label htmlFor="bankButton{vehicle.id}" className={cx(styles.r24CheckBoxInputLabel)}><Translate
                            content={'vehicleSearch1.online'}/></label>
                      </span>
                          <div className={cx(styles.r24FullWidth, styles.r24AlingCenter)}>
                            <div className={'row justify-content-center'}>
                              {banks.map((bank,i) =>
                                  <div className={'col-lg-2 col-md-2 col-3'} key={i}>
                                    <img
                                        className={'bankLogo'}
                                        key={i}
                                        width="75"
                                        height="30" 
                                        src={rootUrl+'/assets/logos/banks/' + bank.name + '.svg'}
                                        onClick={() => {if(payPaytrail) {onCreateReservation(bank.name)}}}
                                        alt={""} loading="lazy"/>
                                  </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                  }

                  {freeReservation &&
                      <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                        <div className={styles.paymentRow}>
                      <span>
                        <input type="radio" name="paymentMethodsSelection" id="freeButton{vehicle.id}"
                               checked={payFree} readOnly value={paymentMethod.FREE} className={styles.r24CheckboxInput}/>
                        <label htmlFor="freeButton{vehicle.id}" className={cx(styles.r24CheckBoxInputLabel)}>
                          <Translate content={'vehicleSearch1.free'} />
                        </label>
                      </span>
                        </div>
                      </div>
                  }
                </div>
                <div className={cx(styles.r24FullWidth, styles.r24Padding10Top0)}>
                  <div className={styles['payment-footer']}>
                    <p><strong><Translate content={'vehicleSearch1.cancellationNotice'} /></strong></p>
                    <p><Translate content={'vehicleSearch1.creditcardPaymentNotice'} /></p>
                  </div>
                </div>
              </form>
            </div>


            <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
            <div>
              <p style={{color: 'red', fontSize: 10, marginBottom: 5, textAlign: "start"}}>
                {agreeErrorString ? agreeErrorString : ""}
              </p>
              <div>
                <CustomerAgreeCheckboxInput formSubmitted={paymentSubmitted}
                                            label={<span>{translateService.translate("vehicleSearch1.gdpr")}
                                              <a target="_blank" rel="noopener noreferrer"
                                                 href={"https://www.24rent.fi/tietosuojaseloste/"}>{translateService.translate("vehicleSearch1.gdpr-link")}</a>).<strong>*</strong></span>}
                                            fieldName={acceptFieldRefs} errorString={""} setValue={setPolicy} checked={false}/>
                <CustomerAgreeCheckboxInput formSubmitted={paymentSubmitted}
                                            label={<span>{translateService.translate("vehicleSearch1.agree")}
                                              <a target="_blank" rel="noopener noreferrer"
                                                 href={"https://www.24rent.fi/vuokrausehdot/"}>{translateService.translate("vehicleSearch1.agree-link")}</a>{" )"}.<strong>*</strong></span>}
                                            fieldName={"acceptField2"} errorString={""} setValue={setConditions}
                                            checked={false}/>
                <CustomerAgreeCheckboxInput formSubmitted={paymentSubmitted}
                                            label={<span>{translateService.translate("vehicleSearch1.understand")}
                                              <a target="_blank" rel="noopener noreferrer"
                                                 href={"https://24rent.zendesk.com/hc/fi/categories/115000177154-Mit%C3%A4-yhteisk%C3%A4ytt%C3%B6autoilu-on-"}>{translateService.translate("vehicleSearch1.understand-link")}</a>).<strong>*</strong></span>}
                                            fieldName={"acceptField3"} errorString={""} setValue={setReturnAgree}
                                            checked={false}/>
                <CustomerAgreeCheckboxInput formSubmitted={paymentSubmitted}
                                            label={<span>{translateService.translate("vehicleSearch1.fairUnderstand")}
                                              <a target="_blank" rel="noopener noreferrer"
                                                 href={'https://www.24rent.fi/en/fair-en/'}>{translateService.translate("vehicleSearch1.fairUnderstand-link")}</a>.<strong>*</strong></span>}
                                            fieldName={"acceptField4"} errorString={""} setValue={setLiability}
                                            checked={false}/>
              </div>
            </div>
            <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
            {!user?.emailConsent &&
                <div className={cx(styles.r24FullWidth, styles.r24BottomMargin10)}>
                  <div id="consent{vehicle.id}" className={cx(styles.r24FullWidth, styles.emailConsentEmptyLeft)}>
                    <p className={styles.bold} style={{marginBottom: '10px'}}><Translate content={'vehicleSearch1.emailConsentQuestion'}/> *</p>
                    <div style={{display: "flex", flexDirection: "row"}}>
                      <CustomerRadioInput
                          formSubmitted={paymentSubmitted}
                          consent={"no"}
                          label={translateService.translate('vehicleSearch1.emailConsentNo')}
                          errorString={emailConsentErrorString}
                          fieldName={emailConsent1FieldRef}
                          setValue={setEmailConsentInputs}
                          value={emailConsent.no}
                      />
                      <CustomerRadioInput
                          formSubmitted={paymentSubmitted}
                          label={translateService.translate('vehicleSearch1.emailConsentYes')}
                          consent={"yes"}
                          fieldName={emailConsent2FieldRef}
                          setValue={setEmailConsentInputs}
                          value={emailConsent.yes}
                      />
                    </div>
                  </div>
                </div>
            }

          </>)
      }

      { saveInfoBtnClicked ?
          <div className={cx(styles.r24FullWidth, styles.r24FullWidthFlexPageSelection)} style={{alignItems: "center"}}>
            {/* This section is only for the submit error message to match + align it perfectly above the Save Information text*/}
            <div className={cx(styles.r24ChildFlex1, styles.r24RightMargin5)}></div>
            <div className={cx(styles.r24ChildFlex4, styles.r24LeftMargin5)}>
              <div className={cx(styles.r24FullWidthFlexPageSubmitError)}>{submitValidationErrorString}</div>
            </div>
          </div>
          : null
      }

      <div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)}>
        <div className={cx(styles.r24ChildFlex1, styles.r24RightMargin5)}>
          <button type="button" className={cx("btn", styles.grayButton)} onClick={e=> {
            if(reservationCreated) {
              clearSelections();
              dispatch({type:"CLEAR_SELECTIONS",payload:null});
              setRedirectHome(true);
            } else {back();
              if(!authHashLogin) {
                sendGaEvent('reservation', 'backToCustomerForm', '', 0)
              } else {
                sendGaEvent('reservation', 'backToBeginReservation', '', 0)
              }
            }
          }}>
            <ArrowBackIcon className={styles.arrowIcon} />
          </button>
        </div>
        <div className={cx(styles.r24ChildFlex4, styles.r24LeftMargin5)}>
          <button disabled={reservationCreated} type="submit" className={cx("btn", styles.orangeButton)}
                  onClick={() => {
                    setSaveInfoBtnClicked(true);
                    if(services.paymentMethod !== undefined) {
                      onCreateReservation();
                    } else {
                      setPaymentMethodSelected(false);
                      document.getElementById('selectPaymentMethod')?.scrollIntoView({behavior: "smooth", block: "center"});
                    }
                  }} >
            {translateService.translate(!freeReservation ? 'vehicleSearch1.reserve' : 'vehicleSearch1.freeReservation').toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  </>
}